import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { Badge } from "./ui/badge";
import BeatPlayer from "./BeatPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOOD_COLORS = {
  Dark: "#6b21a8", Aggressive: "#dc2626", Bouncy: "#f59e0b", Melodic: "#ec4899",
  Chill: "#06b6d4", Hype: "#16a34a", Sad: "#3b82f6", Ethereal: "#8b5cf6"
};

const RecommendedBeats = ({ limit = 6, showTitle = true, compact = false, onShowOnboarding }) => {
  const [beats, setBeats] = useState([]);
  const [source, setSource] = useState("");
  const [reason, setReason] = useState("");
  const [topMoods, setTopMoods] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecs = async () => {
      try {
        const headers = {};
        const token = localStorage.getItem("token");
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await axios.get(`${API}/recommendations?limit=${limit}`, {
          headers,
          withCredentials: true
        });
        setBeats(res.data.beats || []);
        setSource(res.data.source || "trending");
        setReason(res.data.reason || "");
        setTopMoods(res.data.top_moods || []);

        if (res.data.show_onboarding && onShowOnboarding) {
          onShowOnboarding();
        }
      } catch (e) {
        // Fallback to trending
        try {
          const res = await axios.get(`${API}/recommendations/trending?limit=${limit}`);
          setBeats(res.data.beats || []);
          setSource("trending");
          setReason("Popular right now");
        } catch (e2) {
          console.error("Failed to fetch recommendations:", e2);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchRecs();
  }, [limit, onShowOnboarding]);

  if (loading) {
    return (
      <div className="py-8 flex justify-center">
        <Loader2 className="w-6 h-6 text-neon-cyan animate-spin" />
      </div>
    );
  }

  if (beats.length === 0) return null;

  return (
    <div data-testid="recommended-beats">
      {showTitle && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-neon-magenta" />
              <h2 className="font-heading text-xl uppercase tracking-wide">
                {source === "personalized" ? "Recommended For You" : "Trending Now"}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-sm text-zinc-500">{reason}</p>
              {topMoods.map(m => (
                <span key={m} className="text-xs px-2 py-0.5" style={{ background: `${MOOD_COLORS[m] || '#666'}22`, color: MOOD_COLORS[m] || '#666' }}>
                  {m}
                </span>
              ))}
            </div>
          </div>
          <Link to="/discover" className="text-xs text-neon-cyan hover:text-neon-magenta flex items-center gap-1 transition-colors" data-testid="see-all-recs">
            See all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}

      {compact ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {beats.map((beat, idx) => (
            <motion.div
              key={beat.beat_id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Link to={`/beats/${beat.beat_id}`} className="block group" data-testid={`rec-beat-compact-${beat.beat_id}`}>
                <div className="aspect-square relative overflow-hidden mb-2">
                  <img
                    src={beat.image_url || "https://images.unsplash.com/photo-1614149162883-504ce4d13909?w=300"}
                    alt={beat.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 p-2">
                    <p className="text-xs font-medium truncate">{beat.title}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-[10px] text-zinc-500">{beat.bpm} BPM</span>
                      <span className="text-[10px] text-zinc-600">|</span>
                      <span className="text-[10px] text-zinc-500">{beat.genre}</span>
                    </div>
                  </div>
                  {beat.moods?.[0] && (
                    <div className="absolute top-1 right-1">
                      <span className="text-[9px] px-1.5 py-0.5" style={{ background: `${MOOD_COLORS[beat.moods[0]] || '#666'}44`, color: MOOD_COLORS[beat.moods[0]] || '#999' }}>
                        {beat.moods[0]}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-neon-cyan font-mono">${beat.basic_price}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {beats.map((beat, idx) => (
            <motion.div key={beat.beat_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }}>
              <BeatPlayer beat={beat} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecommendedBeats;
